"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

const STORAGE_KEY = "sensei_cookie_consent";

type ConsentValue = "accepted" | "declined" | null;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

function loadClarity() {
  if (typeof window === "undefined" || window.clarity) return;
  // Initialise the Clarity queue
  window.clarity = function (...args: unknown[]) {
    ((window.clarity as unknown as { q?: unknown[] }).q =
      (window.clarity as unknown as { q?: unknown[] }).q || []).push(args);
  };
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://www.clarity.ms/tag/ybzsgcy3qk";
  const y = document.getElementsByTagName("script")[0];
  y.parentNode!.insertBefore(t, y);
}

function updateGtagConsent(granted: boolean) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
      ad_storage: "denied", // we don't run ads
    });
  }
}

export function CookieConsent() {
  const [consent, setConsent] = useState<ConsentValue>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ConsentValue | null;
    if (stored === "accepted" || stored === "declined") {
      setConsent(stored);
      updateGtagConsent(stored === "accepted");
      if (stored === "accepted") loadClarity();
    } else {
      // Slight delay so the banner doesn't flash in during page load
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  function handleAccept() {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setConsent("accepted");
    setVisible(false);
    updateGtagConsent(true);
    loadClarity();
  }

  function handleDecline() {
    localStorage.setItem(STORAGE_KEY, "declined");
    setConsent("declined");
    setVisible(false);
    updateGtagConsent(false);
  }

  // Already decided — render nothing
  if (consent !== null) return null;
  // Not yet shown
  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Cookie consent">
      <div>
        <div className="cookie-banner-inner">
          {/* Icon */}
          <span className="cookie-icon" aria-hidden="true">🍪</span>

          {/* Copy */}
          <div className="cookie-body">
            <p className="cookie-title">We use cookies</p>
            <p className="cookie-desc">
              We use analytics cookies to understand how you use Sensei and improve the experience.
              See our{" "}
              <Link href="/privacy" className="cookie-link">
                Privacy Policy
              </Link>{" "}
              for details.
            </p>
          </div>

          {/* Actions */}
          <div className="cookie-actions">
            <button className="btn btn-sm btn-ghost cookie-decline-btn" onClick={handleDecline}>
              Decline
            </button>
            <button className="btn btn-sm btn-primary" onClick={handleAccept}>
              Accept cookies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
